import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Camera, X } from "lucide-react";
import { USAddressFields } from "../components/USAddressFields";
import { formatApiError, fileUrl } from "../lib/api";
import { compressImage, formatFileSize } from "../lib/imageCompress";
import { uploadWithRetry } from "../lib/uploadRetry";
import { PhoneInput } from "../components/PhoneInput";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND}/api/public`;

const WEEKDAYS = [
  { id: 0, short: "Dom" }, { id: 1, short: "Seg" }, { id: 2, short: "Ter" },
  { id: 3, short: "Qua" }, { id: 4, short: "Qui" }, { id: 5, short: "Sex" }, { id: 6, short: "Sáb" },
];

export default function CollaboratorInvite() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
    phone: "",
    ssn: "",
    payment_method: "",
    payment_id: "",
    photo_path: "",
    workdays: [1, 2, 3, 4, 5],
    address: { street: "", apt_number: "", city: "", state: "", zip_code: "" },
  });

  function upd(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }
  function toggleDay(id) {
    setForm((p) => ({
      ...p,
      workdays: p.workdays.includes(id)
        ? p.workdays.filter((d) => d !== id)
        : [...p.workdays, id].sort(),
    }));
  }

  useEffect(() => {
    let alive = true;
    axios
      .get(`${PUBLIC_API}/invite/${token}`)
      .then((r) => {
        if (!alive) return;
        setMeta(r.data);
        setLoading(false);
        if (r.data.status === "submitted") setSubmitted(true);
      })
      .catch((e) => {
        if (!alive) return;
        setMeta({ status: "invalid", detail: formatApiError(e.response?.data?.detail) || "Link inválido" });
        setLoading(false);
      });
    return () => { alive = false; };
  }, [token]);

  async function uploadPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Foto muito grande (máx 20MB). Use uma imagem menor.");
      return;
    }
    setUploadingPhoto(true);
    setUploadPct(0);
    try {
      const compressed = await compressImage(file, { maxDim: 1280, quality: 0.85 });
      const fd = new FormData();
      fd.append("file", compressed, compressed.name || "photo.jpg");
      const { data } = await uploadWithRetry(
        () =>
          axios.post(`${BACKEND}/api/public/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
              if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
            },
          }),
        {
          onRetry: (attempt) => {
            toast.info(`Conexão instável — tentando novamente (${attempt}/2)…`, {
              duration: 2500,
            });
            setUploadPct(0);
          },
        },
      );
      upd("photo_path", data.path);
      // Feedback visual da compressão (caso tenha rolado)
      const info = compressed._compressionInfo;
      if (info && !info.skipped && info.savedPct >= 30) {
        toast.success(
          `Foto enviada (otimizada: ${formatFileSize(info.originalSize)} → ${formatFileSize(info.finalSize)}, ${info.savedPct}% menor)`,
          { duration: 4500 },
        );
      } else {
        toast.success("Foto enviada");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        formatApiError(detail) ||
          "Não foi possível enviar a foto agora. Você poderá adicioná-la depois.",
      );
    } finally {
      setUploadingPhoto(false);
      setUploadPct(0);
      e.target.value = "";
    }
  }

  // (compressImage agora vive em /lib/imageCompress.js para reuso)

  async function submit(e) {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (!form.address.street || !form.address.zip_code) {
      toast.error("Preencha pelo menos a rua e o ZIP do endereço");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${PUBLIC_API}/invite/${token}/submit`, {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
        address: form.address,
        ssn: form.ssn || null,
        zelle_venmo:
          form.payment_method && form.payment_id
            ? `${form.payment_method}: ${form.payment_id}`
            : null,
        photo_path: form.photo_path || null,
        workdays: form.workdays,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar dados");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 text-slate-500 py-12">
          <Loader2 size={18} className="animate-spin" /> Validando convite…
        </div>
      </Shell>
    );
  }

  if (["invalid", "expired", "revoked"].includes(meta?.status)) {
    return (
      <Shell>
        <div className="text-center py-12">
          <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={28} />
          </div>
          <h2 className="font-bold text-xl text-slate-900">Link inválido ou expirado</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">
            {meta?.detail || "Peça um novo link de convite ao administrador."}
          </p>
        </div>
      </Shell>
    );
  }

  if (submitted || ["submitted", "approved"].includes(meta?.status)) {
    return (
      <Shell>
        <div className="text-center py-10">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={28} />
          </div>
          <h2 className="font-bold text-xl text-slate-900">Dados enviados!</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">
            {meta?.status === "approved"
              ? "Seu cadastro foi aprovado. Aguarde o administrador ativar seu acesso."
              : "Aguarde o administrador aprovar seu cadastro. Você receberá uma notificação quando puder fazer login."}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" data-testid="invite-form">
        <p className="text-sm text-slate-600 -mt-2 mb-2">
          Preencha seus dados abaixo como faria no cadastro normal. Após o envio, o administrador validará e ativará seu acesso ao sistema.
        </p>

        {/* Foto de perfil */}
        <Section title="Foto de perfil">
          <div className="flex items-center gap-4">
            {form.photo_path ? (
              <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-slate-200">
                <img src={fileUrl(form.photo_path)} alt="foto" className="w-full h-full object-cover" />
                <button type="button" onClick={() => upd("photo_path", "")}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow">
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                <Camera size={24} />
              </div>
            )}
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" disabled={uploadingPhoto} />
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs hover:bg-slate-800 disabled:opacity-50">
                {uploadingPhoto ? <><Loader2 size={12} className="animate-spin" /> Enviando…</> : "Selecionar foto"}
              </span>
            </label>
          </div>
          {uploadingPhoto && (
            <div className="mt-3" data-testid="invite-photo-progress">
              <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
                <span>Enviando foto…</span>
                <span className="font-bold text-blue-700">{uploadPct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-150"
                  style={{ width: `${uploadPct}%` }}
                />
              </div>
            </div>
          )}
        </Section>

        {/* Dados pessoais */}
        <Section title="Dados pessoais">
          <Lbl label="Nome completo" req>
            <input data-testid="invite-name" className="cm-input" required
              value={form.name} onChange={(e) => upd("name", e.target.value)} />
          </Lbl>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Lbl label="E-mail (login)" req>
              <input data-testid="invite-email" type="email" className="cm-input" required
                value={form.email} onChange={(e) => upd("email", e.target.value)} />
            </Lbl>
            <Lbl label="Telefone (US)">
              <PhoneInput
                testId="invite-phone"
                value={form.phone}
                onChange={(v) => upd("phone", v)}
              />
            </Lbl>
          </div>
        </Section>

        {/* Senha */}
        <Section title="Senha de acesso">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Lbl label="Senha" req hint="Mínimo 6 caracteres">
              <input data-testid="invite-password" type="password" className="cm-input" required minLength={6}
                value={form.password} onChange={(e) => upd("password", e.target.value)} />
            </Lbl>
            <Lbl label="Confirmar senha" req>
              <input data-testid="invite-password-confirm" type="password" className="cm-input" required minLength={6}
                value={form.confirm} onChange={(e) => upd("confirm", e.target.value)} />
            </Lbl>
          </div>
        </Section>

        {/* Endereço completo (US format) */}
        <Section title="Endereço">
          <USAddressFields
            value={form.address}
            onChange={(v) => upd("address", v)}
            testIdPrefix="invite-addr"
          />
        </Section>

        {/* Documentos / pagamento */}
        <Section title="Documentos & Pagamento">
          <Lbl label="SSN (opcional)" hint="Últimos 4 dígitos">
            <input data-testid="invite-ssn" className="cm-input" maxLength={4}
              value={form.ssn} onChange={(e) => upd("ssn", e.target.value)} />
          </Lbl>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Lbl label="Método de pagamento">
              <select
                data-testid="invite-payment-method"
                className="cm-input"
                value={form.payment_method}
                onChange={(e) => upd("payment_method", e.target.value)}
              >
                <option value="">— Selecione —</option>
                <option value="Zelle">Zelle</option>
                <option value="Venmo">Venmo</option>
              </select>
            </Lbl>
            <div className="sm:col-span-2">
              <Lbl
                label={
                  form.payment_method === "Zelle"
                    ? "E-mail ou telefone do Zelle"
                    : form.payment_method === "Venmo"
                    ? "@usuário do Venmo"
                    : "ID do pagamento"
                }
                hint={!form.payment_method ? "Selecione o método à esquerda" : undefined}
              >
                <input
                  data-testid="invite-payment-id"
                  className="cm-input"
                  placeholder={
                    form.payment_method === "Zelle"
                      ? "email@exemplo.com ou (555) 123-4567"
                      : form.payment_method === "Venmo"
                      ? "@seu-usuario"
                      : ""
                  }
                  disabled={!form.payment_method}
                  value={form.payment_id}
                  onChange={(e) => upd("payment_id", e.target.value)}
                />
              </Lbl>
            </div>
          </div>
        </Section>

        {/* Dias preferidos de trabalho */}
        <Section title="Dias preferidos de trabalho">
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((d) => {
              const selected = form.workdays.includes(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDay(d.id)}
                  data-testid={`invite-workday-${d.id}`}
                  className={`min-w-[44px] h-11 px-3 rounded-full text-xs font-semibold border transition-colors ${
                    selected
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                  }`}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
        </Section>

        <button
          type="submit"
          disabled={submitting}
          data-testid="invite-submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-60"
        >
          {submitting ? "Enviando…" : "Enviar para aprovação"}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-5 sm:p-8 my-6">
        <div className="flex items-center gap-3 mb-4">
          <img src="/assets/cleanmanager-emblem.png" alt="Clean Manager USA"
            className="w-12 h-12 rounded-xl bg-white object-contain" />
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
              Convite de colaborador
            </div>
            <h1 className="font-bold text-xl text-slate-900">Clean Manager USA</h1>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold border-b border-blue-100 pb-1">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Lbl({ label, req, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label} {req && <span className="text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}
